/**
 * @file Étape de sélection de la date dans le flow de réservation.
 * @module packages.common.components.table-reservation.sections.DateStep
 *
 * Affiche un calendrier mensuel navigable avec chargement asynchrone des dates
 * disponibles par mois/service/taille de groupe. Gère le cache des mois déjà chargés,
 * les tentatives de rechargement en cas d'échec, et l'affichage d'un skeleton loader
 * pendant le chargement initial. Récupère également les dates ayant des formules
 * à disponibilité temporelle pour afficher un indicateur visuel sur le calendrier.
 *
 * Quand le mois affiché n'offre aucune place pour le service et le groupe choisis,
 * un bandeau propose les prochaines dates réservables en toutes lettres : le
 * calendrier ne change jamais de mois tout seul, c'est le clic sur une date du
 * bandeau qui le déplace, pour qu'un client ne confonde jamais deux mois.
 */

import React, {useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect} from 'react';
import PropTypes from 'prop-types';
import {format, addMonths, subMonths, isValid, isAfter, startOfMonth, isSameMonth, isSameDay, parseISO} from 'date-fns';
import {logger} from 'packages/common/utils/clientLogger';
import {useLocales} from 'packages/common/locales';

// @mui
import {styled, alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Button from '@mui/material/Button';

// icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

// components
import {ReservationCalendar} from '../components/ReservationCalendar';
import AnnouncementBanner from '../components/AnnouncementBanner';
import NextAvailabilityBanner from '../components/NextAvailabilityBanner';
import OpeningBanner from '../components/OpeningBanner';
import HorizonLegend from '../components/HorizonLegend';

// hooks
import {useNextAvailability} from '../hooks/useNextAvailability';

// API
import {useReservationData} from 'packages/common/components/table-reservation/context/ReservationDataContext';

// constants
import {AVAILABILITY_STATUS, ERROR_MESSAGES} from '../constants/reservation';
import {isBookingClosed, getMaximumBookingDate, isDayOpenForBooking, HORIZON_ROLLING, HORIZON_ANCHOR_MIDNIGHT, DEFAULT_ADVANCE_BOOKING_DAYS} from '../utils/bookingHorizon';
import {findExceptionForDay} from 'packages/common/utils/restaurant/exceptionDayResolver';
import {isBeforeOpeningDay} from 'packages/common/utils/restaurant/preOpening';

// ----------------------------------------------------------------------

// Pas de taille imposée aux textes de l'enveloppe : chaque `Typography` garde
// celle de sa variante, sans contre-règle à poser dans les composants enfants.
const CalendarWrapper = styled(Box)(({theme}) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
}));

const CalendarHeader = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
}));

const MonthNavButton = styled(IconButton)(({theme}) => ({
  'padding': theme.spacing(0.5),
  '&:not(.Mui-disabled)': {
    color: theme.palette.primary.main,
  },
  '&:hover:not(.Mui-disabled)': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
  },
}));


const CalendarSkeletonLoader = styled(Box)(({theme}) => ({
  width: '100%',
  padding: theme.spacing(1),
}));

const CalendarContainer = styled(Box)(({theme}) => ({
  minHeight: 260,
  position: 'relative',
}));

// Dates disponibles, en liste d'attente et fermées par préavis (message téléphone
// à l'étape des horaires) sont toutes sélectionnables.
const SELECTABLE_STATUSES = Object.freeze([
  AVAILABILITY_STATUS.AVAILABLE,
  AVAILABILITY_STATUS.WAITLIST_ONLY,
  AVAILABILITY_STATUS.NOTICE_CLOSED,
]);

// Une « place » au sens du bandeau : un jour où l'on peut vraiment réserver (en
// ligne, ou par téléphone si seul le préavis ferme le canal). La liste d'attente
// n'est pas une place : un mois tout en orange appelle bien le bandeau.
const PLACE_STATUSES = Object.freeze([AVAILABILITY_STATUS.AVAILABLE, AVAILABILITY_STATUS.NOTICE_CLOSED]);

/**
 * Clé de cache d'un mois chargé : la disponibilité dépend du service et du groupe.
 *
 * @param {Date} date - Jour quelconque du mois
 * @param {string} service - Nom du service
 * @param {number} partySize - Nombre de convives
 * @return {string} Clé `année-mois-service-convives`
 */
function buildMonthKey(date, service, partySize) {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${service}-${partySize}`;
}

// ----------------------------------------------------------------------

/**
 * Étape de sélection de la date du calendrier de réservation.
 *
 * Charge les dates disponibles mois par mois via l'API, valide la sélection
 * et transmet les informations de waitlist au formulaire parent.
 *
 * @param {Object} props
 * @param {Object} props.formData - Données du formulaire de réservation
 * @param {Function} props.updateFormData - Met à jour un champ du formulaire
 * @param {Object} [props.config] - Configuration du restaurant (politique de réservation, exceptions)
 * @param {string} [props.companyId] - Identifiant de l'établissement
 * @param {Function} props.setValidation - Enregistre la fonction de validation auprès du parent
 * @param {Function} props.setErrors - Définit les erreurs de validation
 * @param {Object} [props.errors] - Erreurs de validation courantes
 * @param {Function} [props.setStepReady] - Active/désactive le bouton suivant
 * @param {Function} [props.t] - Fonction de traduction générique
 * @param {Function} [props.getCustomText] - Fonction de traduction des textes personnalisés
 * @param {Object} [props.companyDetails] - Établissement (nom affiché dans l'annonce d'ouverture)
 * @param {Function} [props.onContentResize] - Notifie le parent d'un changement de hauteur du contenu
 * @return {JSX.Element}
 */
export function DateStep({
  formData,
  updateFormData,
  config,
  companyId,
  setValidation,
  setErrors,
  errors,
  setStepReady,
  t,
  getCustomText,
  companyDetails = null,
  onContentResize = () => {},
}) {
  const {currentLang} = useLocales();
  const dateFnsLocale = currentLang.adapterLocale;

  const weekdayLabels = useMemo(() => {
    return [
      {abbr: t('widgets.common.weekDaysShort.monday'), full: t('widgets.common.weekDays.monday')},
      {abbr: t('widgets.common.weekDaysShort.tuesday'), full: t('widgets.common.weekDays.tuesday')},
      {abbr: t('widgets.common.weekDaysShort.wednesday'), full: t('widgets.common.weekDays.wednesday')},
      {abbr: t('widgets.common.weekDaysShort.thursday'), full: t('widgets.common.weekDays.thursday')},
      {abbr: t('widgets.common.weekDaysShort.friday'), full: t('widgets.common.weekDays.friday')},
      {abbr: t('widgets.common.weekDaysShort.saturday'), full: t('widgets.common.weekDays.saturday')},
      {abbr: t('widgets.common.weekDaysShort.sunday'), full: t('widgets.common.weekDays.sunday')},
    ];
  }, [t]);

  // Avant-ouverture d'un nouveau restaurant, exposée par le serveur tant qu'elle a un effet.
  const preOpening = config?.preOpening || null;

  // Le mois affiché est restauré depuis la date déjà sélectionnée : en revenant
  // de l'étape créneaux, on retrouve le mois consulté plutôt que le mois courant.
  // Un restaurant pas encore ouvert s'affiche directement sur son mois d'ouverture.
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (formData.date && isValid(formData.date)) return formData.date;
    return isBeforeOpeningDay(preOpening?.openingDate, new Date()) ? parseISO(preOpening.openingDate) : new Date();
  });
  const [availabilityMap, setAvailabilityMap] = useState({});
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [loadedMonths, setLoadedMonths] = useState({});
  const [fetchAttempts, setFetchAttempts] = useState({});
  const [hasFirstLoad, setHasFirstLoad] = useState(false);
  const fetchingRef = useRef(false);
  const calendarRef = useRef(null);
  const [formulaDates, setFormulaDates] = useState(new Set());
  const formulaDatesLoadedRef = useRef({});

  const {getAvailableDates, getFormulaDates} = useReservationData();

  // Focus automatique du calendrier après le premier chargement (accessibilité clavier)
  useLayoutEffect(() => {
    if (hasFirstLoad && !isInitialLoading && calendarRef.current) {
      // Délai pour laisser le Fade terminer son animation avant de déplacer le focus
      const timer = setTimeout(() => {
        calendarRef.current.focus?.();
      }, 450);
      return () => clearTimeout(timer);
    }
  }, [hasFirstLoad, isInitialLoading]);

  const validateStep = useCallback(() => {
    if (!formData.date || !isValid(formData.date)) {
      setErrors({date: ERROR_MESSAGES.DATE_REQUIRED});
      return false;
    }

    const dateKey = format(formData.date, 'yyyy-MM-dd');
    const dateAvailability = availabilityMap[dateKey];
    if (!dateAvailability) {
      setErrors({date: t('widgets.reservation.validation.dateUnavailable')});
      return false;
    }

    if (!SELECTABLE_STATUSES.includes(dateAvailability.status)) {
      setErrors({date: t('widgets.reservation.validation.dateUnavailable')});
      return false;
    }

    return true;
  }, [formData.date, availabilityMap, setErrors, t]);

  useEffect(() => {
    setValidation(validateStep);
  }, [setValidation, validateStep]);

  useEffect(() => {
    if (setStepReady) {
      const isDateSelected = formData.date && isValid(formData.date);
      let isDateAvailable = false;
      if (isDateSelected) {
        const dateKey = format(formData.date, 'yyyy-MM-dd');
        const dateAvailability = availabilityMap[dateKey];
        isDateAvailable = Boolean(dateAvailability) && SELECTABLE_STATUSES.includes(dateAvailability.status);
      }
      setStepReady(isDateSelected && isDateAvailable);
    }
  }, [formData.date, availabilityMap, setStepReady]);

  useEffect(() => {
    if (!companyId || !formData.service || !formData.partySize) {
      return;
    }

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    const monthKey = buildMonthKey(currentMonth, formData.service, formData.partySize);

    if (loadedMonths[monthKey]) {
      setIsInitialLoading(false);
      if (!hasFirstLoad) {
        setHasFirstLoad(true);
      }
      return;
    }

    // Abandon après 2 tentatives échouées pour le même mois
    if (fetchAttempts[monthKey] && fetchAttempts[monthKey] >= 2) {
      setErrors({api: 'Impossible de charger les dates. Veuillez réessayer plus tard.'});
      return;
    }

    if (fetchingRef.current) {
      return;
    }

    fetchingRef.current = true;

    if (errors.date || errors.api) {
      setErrors({});
    }

    setFetchAttempts((prev) => ({
      ...prev,
      [monthKey]: (prev[monthKey] || 0) + 1,
    }));

    const fetchAvailableDates = async () => {
      try {
        const params = {
          service: formData.service,
          partySize: formData.partySize,
          year,
          month,
        };

        const dates = await getAvailableDates(params);

        const newAvailabilityMap = {};

        if (Array.isArray(dates)) {
          dates.forEach((dateInfo) => {
            if (dateInfo.__stats__) return;
            const dateKey = dateInfo.date;

            let status;
            if (dateInfo.full) {
              status = AVAILABILITY_STATUS.FULL;
            } else if (dateInfo.noticeClosed) {
              status = AVAILABILITY_STATUS.NOTICE_CLOSED;
            } else if (dateInfo.waitlistAvailable && !dateInfo.available) {
              status = AVAILABILITY_STATUS.WAITLIST_ONLY;
            } else {
              status = AVAILABILITY_STATUS.AVAILABLE;
            }

            newAvailabilityMap[dateKey] = {
              status,
              waitlistAvailable: dateInfo.waitlistAvailable || false,
              waitlistRemaining: dateInfo.waitlistRemaining,
            };
          });
        }

        setAvailabilityMap((prev) => ({...prev, ...newAvailabilityMap}));
        setLoadedMonths((prev) => ({...prev, [monthKey]: true}));

        if (!hasFirstLoad) {
          setHasFirstLoad(true);
        }
      } catch (error) {
        logger.error('Erreur lors du chargement des dates disponibles:', error.message);
        setErrors({api: ERROR_MESSAGES.GENERAL_ERROR});
        if (!hasFirstLoad) {
          setHasFirstLoad(true);
        }
      } finally {
        setIsInitialLoading(false);
        fetchingRef.current = false;
      }
    };

    fetchAvailableDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, formData.service, formData.partySize, currentMonth]);

  // Réinitialisation du cache formules à chaque changement de service
  const prevServiceRef = useRef(formData.service);
  useEffect(() => {
    if (formData.service !== prevServiceRef.current) {
      prevServiceRef.current = formData.service;
      setFormulaDates(new Set());
      formulaDatesLoadedRef.current = {};
    }
  }, [formData.service]);

  // Charge les dates avec formules temporelles pour afficher l'indicateur sur le calendrier.
  // Annule toute requête en vol si l'utilisateur change rapidement de mois ou de
  // service : le flag `cancelled` empêche d'écrire un état issu d'une réponse
  // obsolète (race condition), et `AbortController` libère la requête réseau côté
  // widget public pour éviter d'inonder l'API anonyme.
  useEffect(() => {
    if (!companyId || !formData.service) return;

    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth() + 1;
    const cacheKey = `${year}-${month}-${formData.service}`;

    if (formulaDatesLoadedRef.current[cacheKey]) return;

    const controller = new AbortController();
    let cancelled = false;

    const fetchFormulaDates = async () => {
      try {
        const dates = await getFormulaDates(
            {service: formData.service, year, month},
            {signal: controller.signal},
        );

        if (cancelled) return;

        if (dates.length > 0) {
          setFormulaDates((prev) => {
            const next = new Set(prev);
            dates.forEach((d) => next.add(d));
            return next;
          });
        }

        formulaDatesLoadedRef.current[cacheKey] = true;
      } catch (err) {
        if (cancelled || err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
        logger.error('Erreur chargement dates formules :', err.message);
      }
    };

    fetchFormulaDates();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [companyId, formData.service, currentMonth, getFormulaDates]);

  // Le squelette n'apparaît que pour un mois jamais chargé : revenir sur un mois
  // en cache affiche la grille immédiatement.
  const goToMonth = (date) => {
    setCurrentMonth(date);
    if (!loadedMonths[buildMonthKey(date, formData.service, formData.partySize)]) {
      setIsInitialLoading(true);
    }
  };

  const handlePrevMonth = () => {
    const prevMonth = subMonths(currentMonth, 1);
    if (!isAfter(startOfMonth(new Date()), startOfMonth(prevMonth))) {
      goToMonth(prevMonth);
    }
  };

  const handleNextMonth = () => goToMonth(addMonths(currentMonth, 1));

  const handleBackToToday = () => goToMonth(new Date());

  // Date choisie dans le bandeau « Prochaines disponibilités » dont le mois n'est
  // pas encore chargé : posée tout de suite (le fil de résumé l'affiche), puis les
  // indicateurs de liste d'attente se complètent dès que ce mois est chargé.
  const pendingPickRef = useRef(null);

  const applyDateSelection = useCallback((date) => {
    // Une sélection explicite annule l'attente du bandeau : sinon un mois déjà en
    // cache laisse le ref armé et le prochain chargement de mois réécrirait la
    // date du bandeau par-dessus le choix du client.
    pendingPickRef.current = null;
    // Un créneau ne vaut que pour sa date : le garder après un changement de jour
    // laisserait poursuivre avec une heure que la nouvelle date ne propose peut-être pas.
    if (formData.date && !isSameDay(formData.date, date)) {
      updateFormData('timeSlot', null);
      updateFormData('selectedTimeSlotData', null);
    }
    updateFormData('date', date);

    const dateKey = format(date, 'yyyy-MM-dd');
    const dateAvailability = availabilityMap[dateKey];
    if (dateAvailability) {
      const isWaitlistDate = dateAvailability.status === AVAILABILITY_STATUS.WAITLIST_ONLY;
      updateFormData('isWaitlistDate', isWaitlistDate);
      // L'étape salle disparaît sur une date en liste d'attente : une salle choisie
      // sur une date précédente filtrerait encore les créneaux et l'inscription.
      if (isWaitlistDate) {
        updateFormData('selectedAreaId', null);
      }
      updateFormData('waitlistInfo', dateAvailability.waitlistAvailable ? {
        remaining: dateAvailability.waitlistRemaining,
      } : null);
    }

    if (errors.date) {
      setErrors({});
    }
  }, [availabilityMap, updateFormData, errors.date, setErrors, formData.date]);

  const handleDateClick = applyDateSelection;

  const handlePickNextDate = (dateStr) => {
    const date = parseISO(dateStr);
    if (availabilityMap[dateStr]) {
      applyDateSelection(date);
    } else {
      pendingPickRef.current = dateStr;
      updateFormData('date', date);
    }
    goToMonth(startOfMonth(date));
  };

  useEffect(() => {
    const pending = pendingPickRef.current;
    if (!pending || !availabilityMap[pending]) return;
    pendingPickRef.current = null;
    applyDateSelection(parseISO(pending));
  }, [availabilityMap, applyDateSelection]);


  const bookingConstraints = useMemo(() => {
    if (!config || !config.bookingPolicy) {
      return {
        horizonMode: HORIZON_ROLLING,
        horizonAnchor: HORIZON_ANCHOR_MIDNIGHT,
        advanceBookingDays: DEFAULT_ADVANCE_BOOKING_DAYS,
        openUntilDate: null,
        minimumNoticeHours: 2,
      };
    }

    return {
      horizonMode: config.bookingPolicy.horizonMode || HORIZON_ROLLING,
      horizonAnchor: config.bookingPolicy.horizonAnchor || HORIZON_ANCHOR_MIDNIGHT,
      advanceBookingDays: config.bookingPolicy.advanceBookingDays || DEFAULT_ADVANCE_BOOKING_DAYS,
      openUntilDate: config.bookingPolicy.openUntilDate || null,
      minimumNoticeHours: config.bookingPolicy.minimumNoticeHours !== undefined ?
        config.bookingPolicy.minimumNoticeHours :
        2,
    };
  }, [config]);

  // Le bandeau ne se déclenche que sur un mois chargé, dans la fenêtre de
  // réservation, sans aucune place, qu'il soit complet ou fermé (congés) : dans
  // les deux cas le client veut savoir quand il pourra réserver. Un mois pas
  // encore ouvert reste expliqué par les infobulles du calendrier.
  // Le serveur renvoie aussi les jours au-delà de la fenêtre de réservation (le
  // calendrier les grise) : un tel jour, presque toujours libre, n'est pas une
  // place puisque le client ne peut pas encore le réserver.
  const monthHasPlace = useMemo(() => {
    const monthPrefix = format(currentMonth, 'yyyy-MM');
    const monthEntries = Object.entries(availabilityMap).filter(([dateKey]) => dateKey.startsWith(monthPrefix));
    const isOpenForBooking = (dateKey) => {
      const day = parseISO(dateKey);
      const exception = findExceptionForDay(config?.exceptions, day);
      return isDayOpenForBooking(bookingConstraints, day, {
        serviceSchedule: config?.serviceSchedule,
        selectedService: formData.service,
        exception: exception && !exception.isClosed ? exception : null,
      });
    };
    return monthEntries.some(([dateKey, info]) => PLACE_STATUSES.includes(info.status) && isOpenForBooking(dateKey));
  }, [availabilityMap, currentMonth, bookingConstraints, config, formData.service]);

  const currentMonthLoaded = Boolean(loadedMonths[buildMonthKey(currentMonth, formData.service, formData.partySize)]);
  const monthWithinHorizon = !isAfter(startOfMonth(currentMonth), getMaximumBookingDate(bookingConstraints));
  const nextAvailabilityEnabled = Boolean(t) && !preOpening && currentMonthLoaded && !isInitialLoading &&
    !monthHasPlace && monthWithinHorizon && !isBookingClosed(bookingConstraints);

  // Aucun jour de départ : le serveur balaie depuis son aujourd'hui (fuseau
  // établissement). Partir du mois affiché ferait annoncer « tout est complet »
  // alors que les jours entre aujourd'hui et ce mois ont encore des places.
  const nextAvailability = useNextAvailability({
    enabled: nextAvailabilityEnabled,
    service: formData.service,
    partySize: formData.partySize,
  });

  const currentMonthDisplay = useMemo(() => {
    const monthName = format(currentMonth, 'MMMM', {locale: dateFnsLocale});
    const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
    return `${capitalizedMonth} ${currentMonth.getFullYear()}`;
  }, [currentMonth, dateFnsLocale]);

  const renderCalendarSkeleton = () => (
    <CalendarSkeletonLoader>
      <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 1}}>
        {[...Array(7)].map((_, index) => (
          <Skeleton
            key={`weekday-${index}`}
            variant="rectangular"
            width={30}
            height={16}
            sx={{borderRadius: 0.5}}
          />
        ))}
      </Box>

      {[...Array(4)].map((_, weekIndex) => (
        <Box
          key={`week-${weekIndex}`}
          sx={{display: 'flex', justifyContent: 'space-between', mb: 1}}
        >
          {[...Array(7)].map((_, dayIndex) => (
            <Skeleton
              key={`day-${weekIndex}-${dayIndex}`}
              variant="circular"
              width={36}
              height={36}
            />
          ))}
        </Box>
      ))}
    </CalendarSkeletonLoader>
  );

  const announcementText = getCustomText ? getCustomText('announcement.text') : '';

  const announcement = config?.announcement && announcementText ? {
    ...config.announcement,
    text: announcementText,
  } : null;

  // Les bandeaux et le calendrier arrivent après la mesure initiale de l'iframe :
  // sans cette notification, la hauteur resterait figée sur l'état d'avant et le
  // contenu déborderait dans une barre de défilement interne.
  const hasAnnouncement = Boolean(announcement);
  useEffect(() => {
    onContentResize();
  }, [onContentResize, isInitialLoading, nextAvailability.status, hasAnnouncement, currentMonth, preOpening]);

  return (
    <Box sx={{p: 0}}>
      <CalendarWrapper sx={{p: 0}}>
        {announcement && (
          <Box sx={{mb: 2}}>
            <AnnouncementBanner announcement={announcement} />
          </Box>
        )}

        {errors.date && (
          <Alert severity="error" sx={{mb: 1}}>
            {errors.date}
          </Alert>
        )}

        {errors.api && (
          <Alert severity="error" sx={{mb: 1}}>
            {errors.api}
          </Alert>
        )}

        {/* Restaurant pas encore ouvert : l'annonce d'ouverture remplace les prochaines disponibilités. */}
        {t && preOpening && (
          <OpeningBanner preOpening={preOpening} restaurantName={companyDetails?.name} dateFnsLocale={dateFnsLocale} t={t} />
        )}

        {t && !preOpening && formData.service && formData.partySize > 0 && (
          <NextAvailabilityBanner
            status={nextAvailability.status}
            result={nextAvailability.result}
            currentMonth={currentMonth}
            service={formData.service}
            partySize={formData.partySize}
            onPickDate={handlePickNextDate}
            dateFnsLocale={dateFnsLocale}
            t={t}
          />
        )}

        <CalendarHeader>
          <Typography variant="subtitle2" sx={{fontWeight: 600}}>
            {currentMonthDisplay}
          </Typography>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
            {!isSameMonth(currentMonth, new Date()) && (
              <Button
                size="small"
                onClick={handleBackToToday}
                sx={{typography: 'caption', textTransform: 'none', fontWeight: 600, minWidth: 0, px: 1, py: 0.25}}
              >
                {t ? t('widgets.common.backToToday') : 'Aujourd\'hui'}
              </Button>
            )}
            <MonthNavButton
              size="small"
              onClick={handlePrevMonth}
              disabled={isAfter(startOfMonth(new Date()), startOfMonth(subMonths(currentMonth, 1)))}
            >
              <ArrowBackIcon fontSize="small" />
            </MonthNavButton>
            <MonthNavButton
              size="small"
              onClick={handleNextMonth}
            >
              <ArrowForwardIcon fontSize="small" />
            </MonthNavButton>
          </Box>
        </CalendarHeader>

        <CalendarContainer>
          {isInitialLoading ? (
            renderCalendarSkeleton()
          ) : (
            <Fade in={hasFirstLoad} timeout={400}>
              <Box>
                <ReservationCalendar
                  ref={calendarRef}
                  currentMonth={currentMonth}
                  availabilityMap={availabilityMap}
                  selectedDate={formData.date}
                  onDateSelect={handleDateClick}
                  bookingConstraints={bookingConstraints}
                  exceptions={config?.exceptions || []}
                  selectedService={formData.service}
                  serviceSchedule={config?.serviceSchedule || {}}
                  compact={true}
                  weekdayLabels={weekdayLabels}
                  waitlistEnabled={config?.waitlistEnabled || false}
                  formulaDates={formulaDates}
                  restaurantOpeningDate={preOpening?.openingDate || null}
                  t={t}
                />
              </Box>
            </Fade>
          )}
        </CalendarContainer>

        <HorizonLegend bookingConstraints={bookingConstraints} t={t} dateFnsLocale={dateFnsLocale} />
      </CalendarWrapper>
    </Box>
  );
}

DateStep.propTypes = {
  formData: PropTypes.object.isRequired,
  updateFormData: PropTypes.func.isRequired,
  config: PropTypes.object,
  companyId: PropTypes.string,
  setValidation: PropTypes.func.isRequired,
  setErrors: PropTypes.func.isRequired,
  errors: PropTypes.object,
  setStepReady: PropTypes.func,
  t: PropTypes.func,
  getCustomText: PropTypes.func,
  companyDetails: PropTypes.object,
  onContentResize: PropTypes.func,
};
